'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as echarts from 'echarts';
import { useProbabilityCone } from '@/hooks/use-probability-cone';
import type { ForecastNodeDatum } from '@/hooks/use-probability-cone';
import type { CryptoKlinePoint } from '@/type/crypto';
import { formatCompactValue } from '@/lib/utils/number';
import type { CryptoRelatedEvent } from '@/hooks/use-crypto-related-event';
import { useCryptoRelatedEvent } from '@/hooks/use-crypto-related-event';
import EChart from '@/components/common/EChart';
import { Skeleton } from '@mui/material';
import { useTranslation } from '@/lib/i18n';
import { formatDateTime } from '@/lib/utils/date';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { useCryptoDetail } from '@/hooks/use-crypto-detail';

interface CryptoPriceChartProps {
  symbol: string;
  supportsCone?: boolean;
  openInSidebar?: boolean;
}

interface EventMarkerDatum {
  value: [string, number];
  markerText: string;
  color: string;
  textColor: string;
  imageUrl: string | null;
  event: CryptoRelatedEvent;
  events: CryptoRelatedEvent[];
  count: number;
}

interface EventTooltipState {
  marker: EventMarkerDatum;
  page: number;
  left: number;
  top: number;
}

interface ForecastLowerCurvePoint {
  time: number;
  price: number;
}

const FORECAST_TOOLTIP_SERIES_NAMES = [
  'Forecast outer upper',
  'Forecast upper',
  'Forecast mid',
  'Forecast lower',
  'Forecast outer lower',
];

const EMPTY_RELATED_EVENTS: CryptoRelatedEvent[] = [];
const EVENT_CLUSTER_WINDOW_MS = 24 * 60 * 60 * 1000;
const EVENT_TOOLTIP_CLASS_NAME = 'crypto-price-chart-event-tooltip';

function formatDate(date: string) {
  const value = new Date(date);
  return `${value.getMonth() + 1}/${value.getDate()}`;
}

function formatKlineDate(timestampSeconds: number) {
  return new Date(timestampSeconds * 1000).toISOString();
}

function formatCurrency(price: number) {
  const minimumFractionDigits = Math.abs(price) >= 100 ? 0 : 2;
  const maximumFractionDigits = Math.abs(price) >= 100 ? 0 : 6;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(price);
}

function getTimeValue(date?: string) {
  if (!date) return undefined;
  const value = new Date(date).getTime();
  return Number.isFinite(value) ? value : undefined;
}

function findNearestForecastNode(nodes: ForecastNodeDatum[], time: number) {
  return nodes.reduce<ForecastNodeDatum | undefined>((nearest, node) => {
    if (!nearest) return node;

    const currentDistance = Math.abs(getTimeValue(node.date)! - time);
    const nearestDistance = Math.abs(getTimeValue(nearest.date)! - time);
    return currentDistance < nearestDistance ? node : nearest;
  }, undefined);
}

function createAxisBounds(values: number[]) {
  const finiteValues = values.filter(Number.isFinite);

  if (!finiteValues.length) {
    return { min: 0, max: 1, interval: 0.25 };
  }

  const minValue = Math.min(...finiteValues);
  const maxValue = Math.max(...finiteValues);
  const range = Math.max(maxValue - minValue, Math.abs(maxValue) * 0.08, 1);
  const padding = range * 0.14;
  const min = Math.max(0, minValue - padding);
  const max = maxValue + padding;
  const interval = (max - min) / 4;

  return { min, max, interval };
}

function getEventTime(event: CryptoRelatedEvent) {
  return event.event_time_start || event.created_at || event.end_date || null;
}

function getEventMarkerAppearance(event: CryptoRelatedEvent) {
  const type = event.event_type?.toLowerCase() || '';

  if (type.includes('crypto') || type.includes('token') || type.includes('bitcoin')) {
    return { markerText: 'B', color: '#F7931A', textColor: '#FFFFFF' };
  }

  if (type.includes('polymarket') || type.includes('prediction')) {
    return { markerText: 'P', color: '#7555F0', textColor: '#111018' };
  }

  return { markerText: 'T', color: '#263346', textColor: '#C9D7EA' };
}

function getEventImageUrl(event: CryptoRelatedEvent) {
  const imageUrl = event.image_url?.trim();
  return imageUrl || null;
}

function createEventMarkers(
  events: CryptoRelatedEvent[],
  axisMin: number,
  axisMax: number,
): EventMarkerDatum[] {
  if (!events.length) return [];

  const markerY = axisMin + (axisMax - axisMin) * 0.08;

  const sortedEvents = events
    .map((event) => {
      const eventTime = getEventTime(event);
      const timestamp = eventTime ? new Date(eventTime).getTime() : Number.NaN;
      const importance = Number(event.importance_score || 0);

      return { event, eventTime, timestamp, importance };
    })
    .filter((item) => item.eventTime && Number.isFinite(item.timestamp))
    .sort((a, b) => a.timestamp - b.timestamp || b.importance - a.importance);

  const groups = sortedEvents.reduce<(typeof sortedEvents)[]>((result, item) => {
    const currentGroup = result.at(-1);
    const previousItem = currentGroup?.at(-1);

    if (
      currentGroup &&
      previousItem &&
      item.timestamp - previousItem.timestamp <= EVENT_CLUSTER_WINDOW_MS
    ) {
      currentGroup.push(item);
    } else {
      result.push([item]);
    }

    return result;
  }, []);

  return groups.slice(-8).map((group) => {
    const latestItem = group.at(-1)!;
    const { event, eventTime } = latestItem;

    return {
      value: [new Date(eventTime!).toISOString(), markerY] as [string, number],
      ...getEventMarkerAppearance(event),
      imageUrl: getEventImageUrl(event),
      event,
      events: group.map((item) => item.event),
      count: group.length,
    };
  });
}

function escapeHtml(value: string) {
  return value.replace(/[&<>"']/g, (char) => {
    const entities: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };

    return entities[char];
  });
}

function createPriceTooltip(date: string, price: number, symbol: string, iconUrl?: string | null) {
  const normalizedSymbol = symbol.trim().toUpperCase();
  const icon = iconUrl
    ? `<img src="${escapeHtml(iconUrl)}" alt="${escapeHtml(normalizedSymbol)}" style="width:20px;height:20px;border-radius:50%;object-fit:cover;display:block;box-shadow:inset 0 0 0 1px rgba(255,255,255,.28);" />`
    : `<span style="display:inline-flex;width:20px;height:20px;border-radius:50%;align-items:center;justify-content:center;background:#FF941A;color:white;font-size:12px;font-weight:800;box-shadow:inset 0 0 0 1px rgba(255,255,255,.28);">${escapeHtml(normalizedSymbol.slice(0, 1) || '$')}</span>`;

  return `
    <div style="width:222px;border-radius:10px;background:#191919;color:#EDEDED;box-shadow:0 14px 34px rgba(0,0,0,.45);font-family:Inter,system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;overflow:hidden;">
      <div style="padding:18px 18px 12px;display:flex;align-items:center;gap:12px;font-size:14px;line-height:1;">
        <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" style="flex:0 0 auto;">
          <rect x="3" y="4" width="10" height="9" rx="1.5" fill="none" stroke="#D7D7D7" stroke-width="1.35"/>
          <path d="M5 2.5v3M11 2.5v3M3.5 7h9" stroke="#D7D7D7" stroke-width="1.35" stroke-linecap="round"/>
        </svg>
        <span>${formatDateTime(date, 'MMM DD, YYYY HH:mm')}</span>
      </div>
      <div style="padding:6px 18px 20px;display:flex;align-items:center;gap:12px;font-size:14px;line-height:1;">
        ${icon}
        <span>${formatCurrency(price)}</span>
      </div>
    </div>
  `;
}

interface EventTooltipCardProps {
  marker: EventMarkerDatum;
  page: number;
  onPageChange: (page: number) => void;
}

function EventTooltipCard({ marker, page, onPageChange }: EventTooltipCardProps) {
  const events = useMemo(
    () =>
      marker.events.toSorted((a, b) => {
        const timeA = getEventTime(a);
        const timeB = getEventTime(b);
        return new Date(timeB || 0).getTime() - new Date(timeA || 0).getTime();
      }),
    [marker.events],
  );
  const pageIndex = Math.max(0, Math.min(page, events.length - 1));
  const event = events[pageIndex] ?? marker.event;
  const imageUrl = getEventImageUrl(event);
  const hasPages = events.length > 1;
  const goToPreviousPage = () => onPageChange(Math.max(pageIndex - 1, 0));
  const goToNextPage = () => onPageChange(Math.min(pageIndex + 1, events.length - 1));

  return (
    <Link href={`/predic/event?id=${event.event_id}`} target="_blank" className="block">
      <div className="relative w-68.25 overflow-hidden rounded-xl border border-white/20 bg-[#050505] font-['Inter',system-ui,-apple-system,BlinkMacSystemFont,'Segoe_UI',sans-serif] text-white shadow-[0_12px_28px_rgba(0,0,0,.5)]">
        {imageUrl ? (
          <img src={imageUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-[linear-gradient(135deg,#111827_0%,#050505_62%,#1F2937_100%)]" />
        )}
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_18%,rgba(0,0,0,.3)_48%,rgba(0,0,0,.92)_100%)]" />
        <div className="relative z-10 flex h-full flex-col justify-between p-3.75">
          <div className="flex flex-col items-start justify-between gap-3">
            <div className="flex h-5 items-center justify-center rounded-sm border border-white/15 bg-black/60 px-1.5 text-xs leading-none text-white uppercase">
              {event.event_type}
            </div>
            <div className="line-clamp-2 font-semibold leading-5.5 text-white [text-shadow:0_1px_2px_rgba(0,0,0,.55)]">
              {event.title}
            </div>
            {hasPages && (
              <div className="flex items-center self-end gap-1.5">
                <button
                  type="button"
                  className="flex size-5 cursor-pointer items-center justify-center rounded-full border border-white/50 bg-black/40 text-white disabled:cursor-default disabled:opacity-40"
                  disabled={pageIndex === 0}
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    goToPreviousPage();
                  }}
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="min-w-7.5 text-center text-[10px] font-bold leading-4.5 text-white [text-shadow:0_1px_2px_rgba(0,0,0,.65)]">
                  {pageIndex + 1}/{events.length}
                </span>
                <button
                  type="button"
                  className="flex size-5 cursor-pointer items-center justify-center rounded-full border border-white/50 bg-black/40 text-white disabled:cursor-default disabled:opacity-40"
                  disabled={pageIndex === events.length - 1}
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    goToNextPage();
                  }}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function CryptoPriceChart({
  symbol,
  supportsCone,
  openInSidebar = false,
}: CryptoPriceChartProps) {
  const { data, isLoading } = useCryptoDetail(symbol, {
    datas: ['detail', 'kline'],
  });
  const chartElementRef = useRef<HTMLDivElement | null>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const eventTooltipRef = useRef<HTMLDivElement>(null);
  const forecastTooltipRef = useRef<HTMLDivElement>(null);
  const [eventTooltip, setEventTooltip] = useState<EventTooltipState | null>(null);
  const { t } = useTranslation();
  const getPointTimestamp = (point: CryptoKlinePoint) =>
    Number(
      (point as CryptoKlinePoint & { t?: number }).timestamp ?? (point as { t?: number }).t ?? 0,
    );
  const getPointPrice = (point: CryptoKlinePoint) =>
    Number((point as CryptoKlinePoint & { p?: number }).price ?? (point as { p?: number }).p ?? 0);
  const klinePoints = useMemo(
    () => data?.kline?.toSorted((a, b) => getPointTimestamp(a) - getPointTimestamp(b)) ?? [],
    [data?.kline],
  );
  const resolvedHistoricalPrices = useMemo(
    () => klinePoints.map((point) => getPointPrice(point)),
    [klinePoints],
  );
  const forecastAnchorPoint = klinePoints.at(-1);
  const {
    showForecast,
    chartData: forecastChartData,
    renderTooltip: renderProbabilityConeTooltip,
  } = useProbabilityCone({
    symbol,
    supportsCone,
    klinePoints,
    anchorPoint: forecastAnchorPoint,
  });

  const { data: relatedEventData } = useCryptoRelatedEvent(symbol);
  const relatedEvents = relatedEventData?.events ?? EMPTY_RELATED_EVENTS;

  const option = useMemo<echarts.EChartsOption>(() => {
    const resolvedDates = klinePoints.map((point) => formatKlineDate(getPointTimestamp(point)));
    const historyDates = resolvedDates;
    const historyData = historyDates.map((date, index) => [date, resolvedHistoricalPrices[index]]);
    const anchorDate = historyDates.at(-1);
    const anchorPrice = Number(historyData.at(-1)?.[1] || 0);
    const historyStartTime = getTimeValue(historyDates[0]);
    const historyEndTime = getTimeValue(anchorDate);
    const forecastTimes = showForecast
      ? forecastChartData.dates
          .map((date) => getTimeValue(date))
          .filter((value): value is number => typeof value === 'number')
      : [];
    const latestForecastTime = forecastTimes.length ? Math.max(...forecastTimes) : undefined;
    const latestTime =
      latestForecastTime !== undefined && historyEndTime !== undefined
        ? Math.max(latestForecastTime, historyEndTime)
        : latestForecastTime;
    const xAxisMin = historyStartTime;
    const xAxisMax = latestTime;
    const priceValues = [...resolvedHistoricalPrices, ...forecastChartData.priceValues];
    const yAxisBounds = createAxisBounds(priceValues);
    const eventMarkers: EventMarkerDatum[] = createEventMarkers(
      relatedEvents,
      yAxisBounds.min,
      yAxisBounds.max,
    );
    const eventMarkerSize = 18;
    const eventMarkerHoverSize = 24;
    const eventSeries: echarts.CustomSeriesOption = {
      name: 'Events',
      type: 'custom',
      data: eventMarkers,
      encode: { x: 0, y: 1 },
      z: 8,
      renderItem: (params, api) => {
        const marker = eventMarkers[params.dataIndex];
        if (!marker) return;

        const [x, y] = api.coord([api.value(0), api.value(1)]);
        const isHover = params.actionType === 'highlight';
        const markerSize = isHover ? eventMarkerHoverSize : eventMarkerSize;
        const radius = markerSize / 2;
        const shouldShowCount = marker.count > 1;
        const badgeRadius = isHover ? 8 : 7;
        const badgeX = x + radius * 0.72;
        const badgeY = y - radius * 0.72;
        const badgeText = marker.count > 99 ? '99+' : String(marker.count);
        const countBadge = shouldShowCount
          ? [
              {
                type: 'circle' as const,
                shape: { cx: badgeX, cy: badgeY, r: badgeRadius },
                style: {
                  fill: '#FF4D4F',
                  stroke: '#111111',
                  lineWidth: 2,
                },
              },
              {
                type: 'text' as const,
                style: {
                  x: badgeX,
                  y: badgeY,
                  text: badgeText,
                  fill: '#FFFFFF',
                  fontSize: marker.count > 9 ? 8 : 9,
                  fontWeight: 800,
                  textAlign: 'center',
                  textVerticalAlign: 'middle',
                },
              },
            ]
          : [];

        if (marker.imageUrl) {
          const imageX = x - radius;
          const imageY = y - radius;

          return {
            type: 'group',
            children: [
              {
                type: 'circle',
                shape: { cx: x, cy: y, r: radius + 2 },
                style: {
                  fill: '#111111',
                  stroke: 'rgba(255,255,255,.24)',
                  lineWidth: 2,
                },
              },
              {
                type: 'image',
                style: {
                  image: marker.imageUrl,
                  x: imageX,
                  y: imageY,
                  width: markerSize,
                  height: markerSize,
                },
                clipPath: {
                  type: 'circle',
                  shape: { cx: x, cy: y, r: radius },
                },
              },
              {
                type: 'circle',
                shape: { cx: x, cy: y, r: radius },
                style: {
                  fill: 'transparent',
                  stroke: 'rgba(0,0,0,.55)',
                  lineWidth: 2,
                },
              },
              ...countBadge,
            ],
          };
        }

        return {
          type: 'group',
          children: [
            {
              type: 'circle',
              shape: { cx: x, cy: y, r: radius },
              style: {
                fill: marker.color,
                stroke: '#111111',
                lineWidth: 2,
              },
            },
            {
              type: 'text',
              style: {
                x,
                y,
                text: marker.markerText,
                fill: marker.textColor,
                fontSize: isHover ? 14 : 11,
                fontWeight: 900,
                textAlign: 'center',
                textVerticalAlign: 'middle',
              },
            },
            ...countBadge,
          ],
        };
      },
      tooltip: {
        show: false,
      },
    };

    return {
      animationDuration: 450,
      backgroundColor: '#030303',
      grid: {
        left: 56,
        right: 6,
        top: 0,
        bottom: 44,
        containLabel: false,
        show: true,
        borderColor: '#242424',
        borderWidth: 2,
      },
      tooltip: {
        trigger: 'axis',
        renderMode: 'html',
        appendToBody: true,
        confine: false,
        padding: 0,
        borderWidth: 0,
        backgroundColor: 'transparent',
        extraCssText: 'box-shadow:none;white-space:normal;',
        formatter: (params: any) => {
          const items = Array.isArray(params) ? params : [params];
          const eventItem = items.find((item) => item.seriesName === 'Events');

          if (eventItem?.data) {
            return '';
          }

          const forecastItem = items.find((item) =>
            FORECAST_TOOLTIP_SERIES_NAMES.includes(item.seriesName),
          );

          if (showForecast && forecastItem) {
            return '';
          }

          const priceItem = items.find((item) => item.seriesName === symbol.toUpperCase());

          if (priceItem?.value) {
            const [date, price] = priceItem.value as [string, number];
            return createPriceTooltip(date, Number(price), symbol, data?.logo_url);
          }

          return '';
        },
        position: (point: number[], _params: any, _dom: unknown, _rect: any, size: any) => {
          const gap = 12;
          const contentWidth = size.contentSize[0];
          const contentHeight = size.contentSize[1];
          const viewWidth = size.viewSize[0];
          const viewHeight = size.viewSize[1];
          const left = Math.max(
            8,
            Math.min(point[0] - contentWidth - gap, viewWidth - contentWidth - 8),
          );
          const top = Math.max(
            8,
            Math.min(point[1] - contentHeight / 2, viewHeight - contentHeight - 8),
          );

          return [left, top];
        },
        axisPointer: {
          type: 'line',
          lineStyle: { color: 'rgba(255,255,255,.42)', width: 1, type: 'dashed' },
        },
      },
      xAxis: {
        type: 'time',
        boundaryGap: [0, 0],
        min: xAxisMin,
        max: xAxisMax,
        axisLine: { lineStyle: { color: '#282828', width: 2 } },
        splitLine: {
          show: false,
          lineStyle: { color: '#242424', width: 1 },
        },
        axisLabel: {
          color: '#9B9B9B',
          formatter: (value: number) => formatDate(new Date(value).toISOString()),
        },
      },
      yAxis: {
        type: 'value',
        min: yAxisBounds.min,
        max: yAxisBounds.max,
        interval: yAxisBounds.interval,
        axisLine: { show: true, lineStyle: { color: '#282828', width: 2 } },
        axisTick: { show: false },
        splitLine: {
          lineStyle: {
            color: '#2A2A2A',
            width: 1,
            type: 'dashed',
            dashOffset: 4,
          },
        },
        axisLabel: {
          color: '#9B9B9B',
          // fontSize: 22,
          // fontWeight: 700,
          formatter: (value: number) => formatCompactValue(value, '$'),
        },
      },
      series: [
        {
          name: symbol.toUpperCase(),
          type: 'line',
          data: historyData,
          showSymbol: false,
          symbol: 'circle',
          symbolSize: 8,
          smooth: false,
          lineStyle: { color: '#58D68A', width: 2 },
          emphasis: {
            focus: 'series',
            itemStyle: { color: '#58D68A', borderWidth: 0 },
          },
          z: 5,
          markPoint: {
            symbol: 'circle',
            symbolSize: 10,
            data:
              showForecast && anchorDate
                ? [{ name: 'Current', coord: [anchorDate, anchorPrice] }]
                : [],
            itemStyle: { color: '#030303', borderColor: '#58D68A', borderWidth: 2 },
            label: { show: false },
          },
        },
        ...forecastChartData.series,
        eventSeries,
      ],
    };
  }, [
    forecastChartData,
    klinePoints,
    renderProbabilityConeTooltip,
    relatedEvents,
    resolvedHistoricalPrices,
    showForecast,
    symbol,
    t,
  ]);

  const handleChartReady = useCallback(
    (chart: echarts.ECharts, chartElement: HTMLDivElement) => {
      chartInstanceRef.current = chart;
      chartElementRef.current = chartElement;

      const forecastTimes = forecastChartData.dates
        .map((date) => getTimeValue(date))
        .filter((value): value is number => typeof value === 'number');
      const forecastStartTime = forecastTimes[0];
      const forecastEndTime = forecastTimes.at(-1);
      const forecastLowerCurvePoints: ForecastLowerCurvePoint[] = [
        ...(forecastStartTime !== undefined && forecastAnchorPoint
          ? [{ time: forecastStartTime, price: getPointPrice(forecastAnchorPoint) }]
          : []),
        ...forecastChartData.nodes
          .map((node) => {
            const time = getTimeValue(node.date);
            return typeof time === 'number' ? { time, price: node.prices[0] } : null;
          })
          .filter((item): item is ForecastLowerCurvePoint => Boolean(item)),
      ];

      const hideForecastTooltip = () => {
        if (!forecastTooltipRef.current) return;

        forecastTooltipRef.current.style.opacity = '0';
        forecastTooltipRef.current.style.pointerEvents = 'none';
      };
      const getForecastXRange = () => {
        const startPixel = chart.convertToPixel({ gridIndex: 0 }, [
          forecastStartTime,
          forecastLowerCurvePoints[0]?.price,
        ]) as [number, number] | undefined;
        const endPixel = chart.convertToPixel({ gridIndex: 0 }, [
          forecastEndTime,
          forecastLowerCurvePoints.at(-1)?.price,
        ]) as [number, number] | undefined;
        const forecastStartX = Number(startPixel?.[0]);
        const forecastEndX = Number(endPixel?.[0]);

        if (!Number.isFinite(forecastStartX) || !Number.isFinite(forecastEndX)) {
          return null;
        }

        return {
          min: Math.min(forecastStartX, forecastEndX),
          max: Math.max(forecastStartX, forecastEndX),
        };
      };
      const showForecastTooltip = (event: { offsetX: number }) => {
        const tooltipEl = forecastTooltipRef.current;
        if (
          !tooltipEl ||
          !showForecast ||
          forecastStartTime === undefined ||
          forecastEndTime === undefined ||
          forecastChartData.nodes.length === 0 ||
          forecastLowerCurvePoints.length < 2
        ) {
          hideForecastTooltip();
          return;
        }

        const forecastXRange = getForecastXRange();
        if (
          !forecastXRange ||
          event.offsetX < forecastXRange.min ||
          event.offsetX > forecastXRange.max
        ) {
          hideForecastTooltip();
          return;
        }

        const dataPoint = chart.convertFromPixel({ gridIndex: 0 }, [
          event.offsetX,
          chartElement.clientHeight / 2,
        ]) as [number, number] | undefined;
        const hoverTime = Number(dataPoint?.[0]);
        if (!Number.isFinite(hoverTime)) {
          hideForecastTooltip();
          return;
        }

        const marker = findNearestForecastNode(forecastChartData.nodes, hoverTime);
        const lastLowerPoint = forecastLowerCurvePoints.at(-1);
        if (!marker || !lastLowerPoint) {
          hideForecastTooltip();
          return;
        }

        const lowerPoint = chart.convertToPixel({ gridIndex: 0 }, [
          forecastEndTime,
          lastLowerPoint.price,
        ]) as [number, number] | undefined;
        const lowerX = Number(lowerPoint?.[0]);
        const lowerY = Number(lowerPoint?.[1]);
        if (!Number.isFinite(lowerX) || !Number.isFinite(lowerY)) {
          hideForecastTooltip();
          return;
        }

        tooltipEl.innerHTML = renderProbabilityConeTooltip(marker, symbol, t);
        tooltipEl.style.opacity = '1';
        tooltipEl.style.pointerEvents = 'auto';

        const tooltipWidth = tooltipEl.offsetWidth || 308;
        const left = Math.max(
          8,
          Math.min(lowerX - tooltipWidth / 2, chartElement.clientWidth - tooltipWidth - 8),
        );
        const top = Math.max(8, lowerY + 12);
        tooltipEl.style.transform = `translate(${left}px, ${top}px)`;
      };
      const handleDocumentMouseMove = (event: MouseEvent) => {
        const tooltipEl = forecastTooltipRef.current;
        if (!tooltipEl || tooltipEl.style.opacity !== '1') return;

        const chartRect = chartElement.getBoundingClientRect();
        const forecastXRange = getForecastXRange();
        const offsetX = event.clientX - chartRect.left;

        if (!forecastXRange || offsetX < forecastXRange.min || offsetX > forecastXRange.max) {
          hideForecastTooltip();
        }
      };

      const hideEventTooltip = () => {
        setEventTooltip(null);
      };
      const positionEventTooltip = (offsetX: number, offsetY: number) => {
        const gap = 12;
        const estimatedWidth = 420;
        const estimatedHeight = 160;
        const chartWidth = chartElement.clientWidth;
        const chartHeight = chartElement.clientHeight;
        const left = Math.max(8, Math.min(offsetX + gap, chartWidth - estimatedWidth - 8));
        const top = Math.max(8, Math.min(offsetY - estimatedHeight / 2, chartHeight - 8));

        return { left, top };
      };
      const handleDocumentClick = (event: MouseEvent) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        if (target.closest(`.${EVENT_TOOLTIP_CLASS_NAME}`)) return;
        if (chartElement.contains(target)) return;

        hideEventTooltip();
      };
      const handleChartBlankClick = (event: { target?: unknown }) => {
        if (event.target) return;

        hideEventTooltip();
      };
      const handleChartElementClick = (params: any) => {
        if (params.seriesName !== 'Events' || !params.data) {
          hideEventTooltip();
          return;
        }

        const nativeEvent = params.event?.event;
        const offsetX = nativeEvent?.offsetX ?? chartElement.clientWidth / 2;
        const offsetY = nativeEvent?.offsetY ?? chartElement.clientHeight / 2;
        const position = positionEventTooltip(offsetX, offsetY);

        setEventTooltip({
          marker: params.data as EventMarkerDatum,
          page: 0,
          ...position,
        });
      };

      document.addEventListener('click', handleDocumentClick);
      document.addEventListener('mousemove', handleDocumentMouseMove);
      chart.getZr().on('mousemove', showForecastTooltip);
      chart.getZr().on('click', handleChartBlankClick);
      chart.on('click', handleChartElementClick);

      return () => {
        document.removeEventListener('click', handleDocumentClick);
        document.removeEventListener('mousemove', handleDocumentMouseMove);
        chart.getZr().off('mousemove', showForecastTooltip);
        chart.getZr().off('click', handleChartBlankClick);
        chart.off('click', handleChartElementClick);
        if (chartInstanceRef.current === chart) {
          chartInstanceRef.current = null;
        }
        if (chartElementRef.current === chartElement) {
          chartElementRef.current = null;
        }
      };
    },
    [
      forecastAnchorPoint,
      forecastChartData.dates,
      forecastChartData.nodes,
      renderProbabilityConeTooltip,
      showForecast,
      symbol,
      t,
    ],
  );

  useEffect(() => {
    if (!eventTooltip || !eventTooltipRef.current || !chartElementRef.current) return;

    const tooltipElement = eventTooltipRef.current;
    const chartElement = chartElementRef.current;
    const tooltipRect = tooltipElement.getBoundingClientRect();
    const chartRect = chartElement.getBoundingClientRect();
    const left = Math.max(8, Math.min(eventTooltip.left, chartRect.width - tooltipRect.width - 8));
    const top = Math.max(8, Math.min(eventTooltip.top, chartRect.height - tooltipRect.height - 8));

    if (left !== eventTooltip.left || top !== eventTooltip.top) {
      setEventTooltip((current) => (current ? { ...current, left, top } : current));
    }
  }, [eventTooltip]);

  if (isLoading) {
    return (
      <div>
        <Skeleton variant="rounded" width="100%" height={280} sx={{ bgcolor: 'grey.800' }} />
      </div>
    );
  }

  return (
    <div className="relative">
      <EChart
        option={option}
        className="h-70 w-full bg-black"
        wrapperClassName="h-70"
        openInSidebar={openInSidebar}
        onChartReady={handleChartReady}
      />
      <div
        ref={forecastTooltipRef}
        className="pointer-events-none absolute left-0 top-0 z-20 opacity-0"
      />
      {eventTooltip && (
        <div
          ref={eventTooltipRef}
          className={`${EVENT_TOOLTIP_CLASS_NAME} absolute z-20`}
          style={{
            left: eventTooltip.left,
            top: eventTooltip.top,
          }}
        >
          <EventTooltipCard
            marker={eventTooltip.marker}
            page={eventTooltip.page}
            onPageChange={(page) =>
              setEventTooltip((current) => (current ? { ...current, page } : current))
            }
          />
        </div>
      )}
    </div>
  );
}
